// src/pages/MFAVerifyPage.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ArrowLeft, AlertCircle, HelpCircle } from 'lucide-react';
import './MFAVerifyPage.css';

const MFAVerifyPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    // Dados passados pelo login
    const { email, password, userId, platform = 'enem' } = location.state || {};

    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showBackupHelp, setShowBackupHelp] = useState(false);

    // Redirecionar se não tiver dados necessários
    React.useEffect(() => {
        if (!email || !password || !userId) {
            navigate('/login');
        }
    }, [email, password, userId, navigate]);

    const handleVerify = async () => {
        if (code.length !== 6 && code.length !== 8) {
            setError('Digite um código de 6 dígitos ou um código de backup de 8 caracteres');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Fazer login com MFA
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    password,
                    mfaToken: code,
                    platform
                })
            });

            const data = await response.json();

            if (response.ok) {
                // Salvar tokens
                localStorage.setItem('accessToken', data.accessToken);
                localStorage.setItem('refreshToken', data.refreshToken);
                localStorage.setItem('userId', data.userId);
                localStorage.setItem('role', data.role);

                // ✅ Atualizar contexto com 3 parâmetros
                await login(data.accessToken, data.refreshToken, {
                    userId: data.userId,
                    username: data.username,
                    email: data.email,
                    role: data.role
                });

                // Redirecionar
                navigate('/dashboard');
            } else {
                setError(data.message || 'Código inválido');
            }
        } catch (err) {
            console.error('Erro ao verificar MFA:', err);
            setError('Erro ao verificar código. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (code.length === 6 || code.length === 8)) {
            handleVerify();
        }
    };

    return (
        <div className="mfavp-page">
            <header className="mfavp-header">
                <button className="mfavp-btn-back" onClick={() => navigate('/login')}>
                    <ArrowLeft size={20} />
                    Voltar
                </button>
                <div className="mfavp-header-logo">
                    <div className="mfavp-logo-text">GRAIA</div>
                </div>
            </header>

            <div className="mfavp-content">
                <div className="mfavp-card">
                    <div className="mfavp-icon-container">
                        <Shield size={48} className="mfavp-shield-icon" />
                    </div>

                    <h1 className="mfavp-title">Autenticação de Dois Fatores</h1>
                    <p className="mfavp-description">
                        Digite o código de 6 dígitos do seu app autenticador
                    </p>

                    <div className="mfavp-form">
                        <input
                            type="text"
                            maxLength={8}
                            placeholder="000000"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase())}
                            onKeyPress={handleKeyPress}
                            className="mfavp-code-input"
                            autoFocus
                        />

                        {error && (
                            <div className="mfavp-error-box">
                                <AlertCircle size={16} />
                                <span className="mfavp-error-text">{error}</span>
                            </div>
                        )}

                        <button
                            className="mfavp-btn-verify"
                            onClick={handleVerify}
                            disabled={loading || (code.length !== 6 && code.length !== 8)}
                        >
                            {loading ? 'Verificando...' : 'Verificar'}
                        </button>
                    </div>

                    <div className="mfavp-help-section">
                        <button
                            className="mfavp-btn-help"
                            onClick={() => setShowBackupHelp(!showBackupHelp)}
                        >
                            <HelpCircle size={18} />
                            Não consegue acessar seu app?
                        </button>

                        {showBackupHelp && (
                            <div className="mfavp-help-box">
                                <p className="mfavp-help-text">
                                    <strong>Use um código de backup</strong>
                                </p>
                                <p className="mfavp-help-text">
                                    Você pode usar um dos códigos de backup de 8 caracteres que foram
                                    fornecidos quando configurou o MFA. Cada código pode ser usado apenas uma vez.
                                </p>
                                <p className="mfavp-help-text">
                                    Digite o código de backup no campo acima.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MFAVerifyPage;